import React, {
  useState,
  useEffect,
  useMemo,
} from "react";
import logo from "../assets/logo.png";
import {
  useNavigate,
} from "react-router-dom";

import {
  Search,
  Plus,
  FileText,
  CalendarClock,
  ShieldAlert,
  ShieldCheck,
  LayoutGrid,
  Table2,
  FileSpreadsheet,
  FileDown,
  Users,
  BadgeDollarSign,
  Car,
  Clock3,
} from "lucide-react";

import {
  getClients,
} from "../api";

import NewClientModal
  from "../components/NewClientModal";

import "../styles/insuranceClients.css";

import jsPDF
  from "jspdf";

import "jspdf-autotable";

import * as XLSX
  from "xlsx";

const InsuranceClients = () => {

  const navigate =
    useNavigate();

  /* =========================================
      STATE
  ========================================= */

  const [clients,
    setClients,
  ] = useState([]);

  const [loading,
    setLoading,
  ] = useState(true);

  const [isModalOpen,
    setIsModalOpen,
  ] = useState(false);

  const [searchTerm,
    setSearchTerm,
  ] = useState("");

  const [startDate,
    setStartDate,
  ] = useState("");

  const [endDate,
    setEndDate,
  ] = useState("");

  const [viewMode,
    setViewMode,
  ] = useState("cards");

  /* =========================================
      FETCH
  ========================================= */

  useEffect(() => {

    fetchClients();

  }, []);

  const fetchClients =
    async () => {

      try {

        setLoading(true);

        const data =
          await getClients();

        setClients(data);

      } catch (error) {

        console.error(error);

      } finally {

        setTimeout(() => {

          setLoading(false);

        }, 1500);

      }

    };

  const refreshClients =
    fetchClients;

  /* =========================================
      HELPERS
  ========================================= */

  const formatDate =
    (dateString) => {

      if (!dateString)
        return "N/A";

      const d =
        new Date(dateString);

      if (
        isNaN(d.getTime())
      ) return "N/A";

      return d.toLocaleDateString(
        "en-GB",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
      );
    };

  const getEffectiveRenewalDate =
    (client) =>

      client.latest_next_renewal_date ||

      client.effective_next_renewal_date ||

      client.renewal_date ||

      null;

  /* =========================================
      POLICY STATUS
  ========================================= */

  const getPolicyStatus =
    (client) => {

      const renewal =
        getEffectiveRenewalDate(
          client
        );

      if (!renewal)
        return "Expired";

      const today =
        new Date();

      const renewalDate =
        new Date(renewal);

      const diffDays =
        Math.ceil(
          (
            renewalDate -
            today
          ) /
            (
              1000 *
              60 *
              60 *
              24
            )
        );

      if (diffDays < 0)
        return "Expired";

      if (diffDays <= 30)
        return "Pending Renewal";

      return "Active";
    };

/* =========================================
    UNPAID PREMIUM
========================================= */

const isPremiumUnpaid =
  (client) => {

    return (

      client.premium_paid === false ||

      client.premium_paid === "NO" ||

      client.premium_paid === "No" ||

      client.premium_paid === "no" ||

      client.premium_paid === 0 ||

      client.premium_paid === null

    );
  };


  /* =========================================
      FILTERED
  ========================================= */

  const filteredClients =
    useMemo(() => {

      return clients

        .filter(
          (client) =>

            client.name
              ?.toLowerCase()
              .includes(
                searchTerm.toLowerCase()
              ) ||

            client.policy_number
              ?.toLowerCase()
              .includes(
                searchTerm.toLowerCase()
              ) ||

            client.vehicle_number
              ?.toLowerCase()
              .includes(
                searchTerm.toLowerCase()
              )
        )

        .filter(
          (client) => {

            const eff =
              getEffectiveRenewalDate(
                client
              );

            if (!eff)
              return false;

            const clientDate =
              new Date(eff);

            const start =
              startDate
                ? new Date(
                    startDate
                  )
                : null;

            const end =
              endDate
                ? new Date(
                    endDate
                  )
                : null;

            return (

              (!start ||
                clientDate >=
                  start) &&

              (!end ||
                clientDate <=
                  end)

            );
          }
        );

    }, [
      clients,
      searchTerm,
      startDate,
      endDate,
    ]);

  /* =========================================
      DISPLAY LOGIC
  ========================================= */

  const displayClients =

    searchTerm.trim() === ""

      ? [...clients]

          .sort(
            (a, b) =>
              b.id - a.id
          )

          .slice(0, 10)

      : filteredClients;

  /* =========================================
      STATS
  ========================================= */

  const totalPremium =
    filteredClients.reduce(
      (sum, c) =>
        sum +
        (
          parseFloat(
            c.premium
          ) || 0
        ),
      0
    );

  const activeCount =
    clients.filter(
      (c) =>
        getPolicyStatus(c) ===
        "Active"
    ).length;

  const pendingCount =
    clients.filter(
      (c) =>
        getPolicyStatus(c) ===
        "Pending Renewal"
    ).length;

  const expiredCount =
    clients.filter(
      (c) =>
        getPolicyStatus(c) ===
        "Expired"
    ).length;

  /* =========================================
      NAVIGATION
  ========================================= */

  const handleRowClick =
    (clientId) => {

      navigate(
        `/client-account/${clientId}`
      );
    };

  /* =========================================
      EXPORT PDF
  ========================================= */

  const exportToPDF =
    () => {

      const doc =
        new jsPDF({
          orientation:
            "landscape",
        });

      doc.text(
        "Insurance Clients Report",
        20,
        12
      );

      const tableData =
        filteredClients.map(
          (c) => [

            c.name,

            c.policy_number,

            c.vehicle_number,

            c.insurer,

            c.policy_type,

            c.premium,

            formatDate(
              getEffectiveRenewalDate(
                c
              )
            ),

            getPolicyStatus(c),

          ]
        );

      doc.autoTable({

        head: [[

          "Client",

          "Policy #",

          "Vehicle",

          "Insurer",

          "Type",

          "Premium",

          "Renewal",

          "Status",

        ]],

        body: tableData,

        startY: 22,

      });

      doc.save(
        "Insurance_Report.pdf"
      );
    };

  /* =========================================
      EXPORT EXCEL
  ========================================= */

  const exportToExcel =
    () => {

      const data =
        filteredClients.map(
          (c) => ({

            Client:
              c.name,

            Policy:
              c.policy_number,

            Vehicle:
              c.vehicle_number,

            Insurer:
              c.insurer,

            Premium:
              c.premium,

            Renewal:
              formatDate(
                getEffectiveRenewalDate(
                  c
                )
              ),

            Status:
              getPolicyStatus(c),

          })
        );

      const ws =
        XLSX.utils.json_to_sheet(
          data
        );

      const wb =
        XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        wb,
        ws,
        "Insurance"
      );

      XLSX.writeFile(
        wb,
        "Insurance_Report.xlsx"
      );
    };

  /* =========================================
      LOADING
  ========================================= */

if (loading) {

  return (

    <div className="insurance-modern-loading">

      <div className="insurance-modern-loader">

        <div className="insurance-modern-ring" />

        <div className="insurance-modern-ring-two" />

        <div className="insurance-modern-loader-center">

          <img
            src={logo}
            alt="RCS Logo"
          />

        </div>

      </div>

      <h2>
        Loading Insurance
      </h2>

      <p>

        Preparing policy
        records, renewals
        and client activity...

      </p>

    </div>
  );
}
  /* =========================================
      RENDER
  ========================================= */

  return (

    <div className="insurance-modern-page">

      {/* HERO */}

      <div className="insurance-modern-hero">

        <div className="insurance-modern-hero-left">

          <div className="insurance-modern-badge">

            <ShieldCheck
              size={15}
            />

            INSURANCE MANAGEMENT

          </div>

          <h1>

            Insurance
            <br />

            Portfolio

          </h1>

          <p>

            Monitor policies,
            renewals, premiums
            and insurance client
            activity across the
            RCS ecosystem.

          </p>

          <div className="insurance-modern-actions">

            <button
              className="insurance-modern-primary-btn"
              onClick={() =>
                setIsModalOpen(
                  true
                )
              }
            >

              <Plus
                size={18}
              />

              New Client

            </button>

          </div>

        </div>

        {/* RIGHT */}

        <div className="insurance-modern-hero-right">

          <div className="insurance-modern-stat-card">

            <div className="insurance-modern-stat-icon teal">

              <Users
                size={20}
              />

            </div>

            <h3>
              {clients.length}
            </h3>

            <p>
              Total Policies
            </p>

          </div>

          <div className="insurance-modern-stat-card">

            <div className="insurance-modern-stat-icon green">

              <ShieldCheck
                size={20}
              />

            </div>

            <h3>
              {activeCount}
            </h3>

            <p>
              Active Policies
            </p>

          </div>

          <div className="insurance-modern-stat-card">

            <div className="insurance-modern-stat-icon amber">

              <CalendarClock
                size={20}
              />

            </div>

            <h3>
              {pendingCount}
            </h3>

            <p>
              Pending Renewals
            </p>

          </div>

          <div className="insurance-modern-stat-card">

            <div className="insurance-modern-stat-icon red">

              <ShieldAlert
                size={20}
              />

            </div>

            <h3>
              {expiredCount}
            </h3>

            <p>
              Expired Policies
            </p>

          </div>

        </div>

      </div>

      {/* SEARCH */}

      <div className="insurance-modern-search-wrap">

        <div className="insurance-modern-search-box">

          <Search
            size={20}
          />

          <input
            type="text"
            placeholder="Search by client name, vehicle number or policy number..."
            value={searchTerm}
            onChange={(e) =>
              setSearchTerm(
                e.target.value
              )
            }
          />

        </div>

      </div>

      {/* TOOLBAR */}

      <div className="insurance-modern-toolbar">

        <div className="insurance-modern-toolbar-left">

          <button
            className="insurance-modern-chip"
            onClick={() =>
              navigate(
                "/unpaid-premiums"
              )
            }
          >

            Unpaid

          </button>

          <button
            className="insurance-modern-chip"
            onClick={() =>
              navigate(
                "/upcoming-renewals"
              )
            }
          >

            Upcoming

          </button>

          <button
            className="insurance-modern-chip"
            onClick={() =>
              navigate(
                "/expired-policies"
              )
            }
          >

            Expired

          </button>

          <button
            className="insurance-modern-chip"
            onClick={() =>
              navigate(
                "/appendix"
              )
            }
          >

            Appendix

          </button>

        </div>

        {/* RIGHT */}

        <div className="insurance-modern-toolbar-right">

          <input
            type="date"
            value={startDate}
            onChange={(e) =>
              setStartDate(
                e.target.value
              )
            }
          />

          <input
            type="date"
            value={endDate}
            onChange={(e) =>
              setEndDate(
                e.target.value
              )
            }
          />

          <button
            className="insurance-modern-export-btn"
            onClick={
              exportToPDF
            }
          >

            <FileDown
              size={16}
            />

            PDF

          </button>

          <button
            className="insurance-modern-export-btn"
            onClick={
              exportToExcel
            }
          >

            <FileSpreadsheet
              size={16}
            />

            Excel

          </button>

        </div>

      </div>

      {/* VIEW TOGGLE */}

      <div className="insurance-modern-view-toggle">

        <button
          className={
            viewMode ===
            "cards"

              ? "active"

              : ""
          }
          onClick={() =>
            setViewMode(
              "cards"
            )
          }
        >

          <LayoutGrid
            size={16}
          />

          Cards

        </button>

        <button
          className={
            viewMode ===
            "table"

              ? "active"

              : ""
          }
          onClick={() =>
            setViewMode(
              "table"
            )
          }
        >

          <Table2
            size={16}
          />

          Table

        </button>

      </div>

      {/* EMPTY STATE */}

      {displayClients.length ===
        0 && (

        <div className="insurance-modern-empty">

          <Search
            size={52}
          />

          <h2>
            Search Insurance
            Clients
          </h2>

          <p>

            Search by policy
            number, vehicle
            number or client
            name.

          </p>

        </div>

      )}

      {/* CARDS */}

      {viewMode ===
        "cards" && (

        <div className="insurance-modern-grid">

          {displayClients.map(
            (client) => {

              const status =
                getPolicyStatus(
                  client
                );

              return (

                <div
                  key={client.id}
                  className={`insurance-modern-card 
${status.toLowerCase().replaceAll(" ", "-")}
${isPremiumUnpaid(client) ? " unpaid-policy" : ""}
`}
                  onClick={() =>
                    handleRowClick(
                      client.id
                    )
                  }
                >

                  {/* HEADER */}

                  <div className="insurance-modern-card-header">

                    <div>

                      <h3>
                        {
                          client.name
                        }
                      </h3>

                      <span>

                        {
                          client.insurer
                        }

                      </span>

{isPremiumUnpaid(client) && (

  <div className="insurance-modern-unpaid-alert">

    PREMIUM OUTSTANDING

  </div>

)}

                    </div>

                    <div className={`insurance-modern-status ${status.toLowerCase().replaceAll(" ", "-")}`}>

                      {status}

                    </div>

                  </div>

                  {/* BODY */}

                  <div className="insurance-modern-card-body">

                    <div className="insurance-modern-meta">

                      <FileText
                        size={15}
                      />

                      {
                        client.policy_number
                      }

                    </div>

                    <div className="insurance-modern-meta">

                      <Car
                        size={15}
                      />

                      {
                        client.vehicle_number
                      }

                    </div>

                    <div className="insurance-modern-meta">

                      <Clock3
                        size={15}
                      />

                      {formatDate(
                        getEffectiveRenewalDate(
                          client
                        )
                      )}

                    </div>

                  </div>

                  {/* FOOTER */}

                  <div className="insurance-modern-card-footer">

                    <div className="insurance-modern-premium">

                      <BadgeDollarSign
                        size={16}
                      />

                      $
                      {parseFloat(
                        client.premium || 0
                      ).toLocaleString()}

                    </div>

                    <div className="insurance-modern-policy-type">

                      {
                        client.policy_type
                      }

                    </div>

                  </div>

                </div>
              );
            }
          )}

        </div>

      )}

      {/* TABLE */}

      {viewMode ===
        "table" && (

        <div className="insurance-modern-table-wrap">

          <table className="insurance-modern-table">

            <thead>

              <tr>

                <th>
                  Client
                </th>

                <th>
                  Policy #
                </th>

                <th>
                  Vehicle
                </th>

                <th>
                  Insurer
                </th>

                <th>
                  Premium
                </th>

                <th>
                  Renewal
                </th>

                <th>
                  Status
                </th>

              </tr>

            </thead>

            <tbody>

              {displayClients.map(
                (client) => {

                  const status =
                    getPolicyStatus(
                      client
                    );

                  return (

                    <tr
                      key={
                        client.id
                      }
                      onClick={() =>
                        handleRowClick(
                          client.id
                        )
                      }
                    >

                      <td>
                        {
                          client.name
                        }
                      </td>

                      <td>
                        {
                          client.policy_number
                        }
                      </td>

                      <td>
                        {
                          client.vehicle_number
                        }
                      </td>

                      <td>
                        {
                          client.insurer
                        }
                      </td>

                      <td>

                        $

                        {parseFloat(
                          client.premium || 0
                        ).toLocaleString()}

                      </td>

                      <td>

                        {formatDate(
                          getEffectiveRenewalDate(
                            client
                          )
                        )}

                      </td>

                      <td>

                        <span className={`insurance-modern-status ${status.toLowerCase().replaceAll(" ", "-")}`}>

                          {status}

                        </span>

                      </td>

                    </tr>
                  );
                }
              )}

            </tbody>

          </table>

        </div>

      )}

      {/* MODAL */}

      {isModalOpen && (

        <NewClientModal
          onClose={() =>
            setIsModalOpen(
              false
            )
          }
          refreshClients={
            refreshClients
          }
        />

      )}

    </div>
  );
};

export default InsuranceClients;